import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';
import CreditBalance from '../components/CreditBalance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Credit-based pricing page.
 *
 * Three sections, in order of intent:
 *   1. Your current usage (balance + tier + this month's refill)
 *   2. Subscriptions (recurring credits + perks). The pitch is monthly
 *      capability, not a paywall.
 *   3. Credit packs (one-off top-ups for power users)
 *
 * Tone: "discover what you can do with more." No FOMO bars or shame.
 */
export default function PricingPage() {
  const { token, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [pricing, setPricing] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    axios.get(`${API}/credits/pricing`).then((r) => setPricing(r.data));
    if (token) {
      axios.get(`${API}/credits/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => setMe(r.data)).catch(() => {});
    }
  }, [token]);

  const startCheckout = async (kind, key) => {
    if (!isAuthenticated) {
      navigate('/onboarding');
      return;
    }
    try {
      const url = kind === 'pack' ? '/billing/checkout/pack' : '/billing/checkout/subscription';
      const body = kind === 'pack' ? { sku: key } : { tier: key };
      const res = await axios.post(`${API}${url}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    }
  };

  if (!pricing) return <Loader theme={theme} />;

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-5xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="opacity-70 hover:opacity-100">← Back</button>
        <Logo size={28} />
        <div className="flex-1" />
        {isAuthenticated && <CreditBalance />}
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-8">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight">Pricing</h1>
          <p className="opacity-70 max-w-2xl mt-2">
            Cricket Refreshed runs on insight credits. Every AI action costs a
            small number of credits. Free tier includes 50/month — enough to
            chat with your Coach Twin almost daily. Upgrade for more capacity
            or top up à la carte.
          </p>
        </div>

        {me && <UsageCard me={me} theme={theme} />}

        <Section title="Subscribe" subtitle="Recurring monthly credits + perks">
          <div className="grid md:grid-cols-3 gap-4">
            {pricing.plans
              .filter((p) => p.tier !== 'free')
              .map((p) => (
                <PlanCard
                  key={p.tier} plan={p}
                  active={me?.tier === p.tier}
                  onSubscribe={() => startCheckout('sub', p.tier)}
                  theme={theme}
                />
              ))}
          </div>
        </Section>

        <Section title="Credit packs" subtitle="One-time, never expire">
          <div className="grid md:grid-cols-3 gap-4">
            {pricing.packs.map((pack) => (
              <PackCard
                key={pack.sku} pack={pack}
                onBuy={() => startCheckout('pack', pack.sku)}
                theme={theme}
              />
            ))}
          </div>
        </Section>

        <Section title="What each action costs">
          <CostTable costs={pricing.feature_costs} theme={theme} />
        </Section>
      </main>
    </div>
  );
}

// ---------- pieces ----------

function UsageCard({ me, theme }) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.12), rgba(0,217,255,0.10))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div>
        <div className="text-xs opacity-70 uppercase tracking-widest font-bold">Your balance</div>
        <div className="text-4xl font-extrabold mt-1 tabular-nums">
          {me.balance} <span className="text-base font-normal opacity-60">credits</span>
        </div>
      </div>
      <div className="text-right">
        <div className="text-xs opacity-70 uppercase tracking-widest font-bold">Plan</div>
        <div className="text-xl font-bold">{me.tier_label}</div>
        {me.monthly_credits > 0 && (
          <div className="text-xs opacity-70 mt-0.5">{me.monthly_credits} credits / month</div>
        )}
      </div>
    </div>
  );
}

function PlanCard({ plan, active, onSubscribe, theme }) {
  const featured = plan.tier === 'family_pro';
  return (
    <div
      className="rounded-2xl p-5 flex flex-col h-full"
      style={{
        background: featured
          ? 'linear-gradient(135deg, rgba(0,201,167,0.12), rgba(0,217,255,0.08))'
          : (theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)'),
        border: `1px solid ${featured ? 'rgba(0,201,167,0.4)' : 'rgba(150,160,180,0.2)'}`,
      }}
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-lg font-bold">{plan.label}</div>
        {featured && (
          <span className="text-[10px] uppercase tracking-widest font-bold"
                style={{ color: '#00C9A7' }}>Most popular</span>
        )}
      </div>
      <div className="text-3xl font-extrabold">
        ${(plan.price_usd_cents / 100).toFixed(2)}
        <span className="text-sm opacity-60 font-normal"> / mo</span>
      </div>
      <div className="text-sm opacity-80 mt-1 mb-3">
        {plan.monthly_credits} credits every month
      </div>
      <ul className="space-y-1.5 text-sm mb-5 flex-1">
        {plan.capabilities.map((c) => (
          <li key={c} className="flex items-start gap-2">
            <span style={{ color: '#00C9A7' }}>✓</span>
            <span className="opacity-90">{prettyCap(c)}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onSubscribe}
        disabled={active}
        className="w-full py-2.5 rounded-lg font-semibold text-white"
        style={{
          background: active
            ? 'rgba(150,160,180,0.3)'
            : 'linear-gradient(135deg, #00A651, #00D9FF)',
        }}
      >
        {active ? 'Current plan' : 'Subscribe'}
      </button>
    </div>
  );
}

function PackCard({ pack, onBuy, theme }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(150,160,180,0.2)',
      }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-2xl font-extrabold">{pack.credits}</div>
        <div className="text-sm opacity-70">credits</div>
      </div>
      <div className="text-xl font-bold mb-1">
        ${(pack.price_usd_cents / 100).toFixed(2)}
      </div>
      {pack.bonus_label && (
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2"
             style={{ color: '#00C9A7' }}>{pack.bonus_label}</div>
      )}
      <button
        onClick={onBuy}
        className="w-full py-2 rounded-lg font-semibold mt-2"
        style={{
          background: 'rgba(0,201,167,0.12)',
          color: '#00C9A7',
          border: '1px solid rgba(0,201,167,0.35)',
        }}
      >
        Buy
      </button>
    </div>
  );
}

function CostTable({ costs, theme }) {
  const items = [
    { key: 'chat',          label: 'Coach Twin message', icon: '💬' },
    { key: 'search',        label: 'AI search query',    icon: '🔎' },
    { key: 'match_card',    label: 'Match-day card',     icon: '🎴' },
    { key: 'feedback',      label: 'Session feedback',   icon: '🧠' },
    { key: 'pulse',         label: 'Weekly Pulse',       icon: '📊' },
    { key: 'plan',          label: '7-day practice plan',icon: '🗺️' },
    { key: 'video_analysis',label: 'Video AI analysis',  icon: '🎥' },
  ];
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(150,160,180,0.2)',
      }}
    >
      {items.map((it, i) => (
        <div
          key={it.key}
          className="flex items-center justify-between px-4 py-2.5"
          style={{ borderTop: i === 0 ? 'none' : '1px solid rgba(150,160,180,0.1)' }}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">{it.icon}</span>
            <span>{it.label}</span>
          </div>
          <span className="font-bold tabular-nums">
            {costs[it.key] || 1} credit{(costs[it.key] || 1) > 1 ? 's' : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm opacity-70 mb-3 mt-0.5">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Loader({ theme }) {
  return (
    <div className="min-h-screen flex items-center justify-center"
         style={{ background: theme === 'dark' ? '#060818' : '#f8fafc' }}>
      <Logo size={48} />
    </div>
  );
}

function prettyCap(cap) {
  return ({
    journal: 'Unlimited journaling',
    coach_twin: 'Coach Twin chat (1 credit/msg)',
    load_monitor: 'Load monitor & ACWR alerts',
    basic_radar: 'Basic skill radar',
    skill_radar_cohort: 'Skill radar with peer cohort',
    weekly_pulse: 'Saturday Weekly Pulse',
    priority_queue: 'Priority AI processing',
    pro_badge: 'Pro badge on your profile',
    multi_athlete_dashboard: 'Multi-athlete family dashboard',
    weekly_recap_email: 'Weekly family recap email',
    saturday_pulse_email: 'Saturday Pulse delivered by email',
    match_card_unlimited: 'Unlimited match cards',
    all_athlete_pro: 'Everything in Athlete Pro',
    drill_library: 'Coach drill library + private drills',
    task_assignment: 'Assign drills as tasks',
    student_roster_30: 'Roster up to 30 students',
    review_queue: 'Journal review queue',
    '1_video_per_month': '1 AI video analysis per month',
  })[cap] || cap.replace(/_/g, ' ');
}
